<script setup lang="ts">
import { ChevronDown } from 'lucide-vue-next'
import { computed, ref } from 'vue'

import UiIcon from '@/components/common/UiIcon.vue'
import NavigationLink from '@/components/navigation/NavigationLink.vue'
import type { NavigationItem, NavigationType } from '@/models/navigation'

defineOptions({
  name: 'MobileNavigationItem',
})

const props = withDefaults(defineProps<{
  item: NavigationItem
  navigationType: NavigationType
  depth?: number
}>(), {
  depth: 0,
})

const emit = defineEmits<{
  navigate: []
}>()

const isOpen = ref(false)
const hasChildren = computed(() => props.item.children.length > 0)
const isExtended = computed(() => props.navigationType === 'extended')
const hasDestination = computed(() => Boolean(props.item.relativeUrl || props.item.url))
</script>

<template>
  <li>
    <div
      class="group flex items-center rounded-xl border border-border bg-card text-card-foreground transition-colors hover:border-primary"
      :class="depth > 0 ? 'border-0 bg-transparent' : ''"
    >
      <NavigationLink
        :item="item"
        class="flex min-w-0 flex-1 items-center gap-3 px-4 py-4 text-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
        :class="depth > 0 ? 'py-3 text-base' : ''"
        @click="hasDestination ? emit('navigate') : undefined"
      >
        <span
          v-if="isExtended && item.icon"
          class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-deep"
        >
          <UiIcon
            :name="item.icon"
            class="size-5"
          />
        </span>
        <span class="min-w-0">
          <span class="block">{{ item.label }}</span>
          <span
            v-if="isExtended && item.description"
            class="mt-1 block text-sm font-normal leading-relaxed text-muted-foreground"
          >
            {{ item.description }}
          </span>
        </span>
      </NavigationLink>

      <button
        v-if="hasChildren"
        type="button"
        class="mr-2 flex size-11 shrink-0 items-center justify-center rounded-lg text-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        :aria-label="item.label"
        :aria-expanded="isOpen"
        @click="isOpen = !isOpen"
      >
        <ChevronDown
          :size="20"
          aria-hidden="true"
          class="transition-transform duration-200"
          :class="isOpen ? 'rotate-180' : ''"
        />
      </button>
    </div>

    <ul
      v-if="hasChildren && isOpen"
      class="ml-4 mt-2 space-y-2 border-l border-border pl-3"
    >
      <MobileNavigationItem
        v-for="child in item.children"
        :key="child.id"
        :item="child"
        :navigation-type="navigationType"
        :depth="depth + 1"
        @navigate="emit('navigate')"
      />
    </ul>
  </li>
</template>
